import React from 'react';
import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SensorsIcon from '@mui/icons-material/Sensors';

/**
 * Unified colored status pill for every listings page. Maps a status
 * string to a tone (success / error / warning / info / neutral) using
 * the canonical training / deployment / inference status vocabulary,
 * with sensible fallbacks for arbitrary tones.
 *
 * Props:
 *   status   - the raw status string (rendered as the label by default)
 *   tone     - explicit override: 'success' | 'error' | 'warning' | 'info' | 'neutral'
 *   label    - optional override for the displayed text
 *   tooltip  - optional tooltip
 *   icon     - optional override icon (component); pass `null` to hide
 *   size     - 'sm' (default) | 'md'
 *   variant  - 'soft' (default) | 'outlined' | 'solid'
 *   sx       - extra sx
 */

// Canonical status -> tone map. Anything not listed falls through to
// ``neutral`` and rendered as a soft grey pill so unrecognised values
// remain readable without a misleading colour.
const STATUS_TONE = {
  // Training / generic terminal states
  success: 'success',
  completed: 'success',
  ok: 'success',
  ready: 'success',

  failed: 'error',
  error: 'error',
  cancelled: 'error',

  warning: 'warning',
  queued: 'warning',
  skipped: 'warning',
  paused: 'warning',
  archived: 'warning',

  started: 'info',
  running: 'info',
  in_progress: 'info',
  training: 'info',
  deploying: 'info',
  pending: 'info',

  unknown: 'neutral',
  idle: 'neutral',
  draft: 'neutral',

  // Goal serving state (a deployed champion is producing forecasts). Note:
  // this is the *operational* state and is distinct from "live data" (whether
  // the goal's data streams in). ``live`` is kept as a legacy alias for goals
  // persisted before the rename and renders identically to ``serving``.
  serving: 'success',
  live: 'success',

  // Deployments
  deployed: 'success',
  active: 'success',
  inactive: 'neutral',
  promoted: 'success',
  rolled_back: 'warning',
};

// Display-text overrides where the raw status word would be misleading.
// Legacy ``live`` goal-status values predate the data/serving split; show
// them as "Serving" so the word "live" is reserved for the data dimension.
const STATUS_LABEL = {
  live: 'Serving',
};

const STATUS_ICON = {
  success: CheckCircleIcon,
  completed: CheckCircleIcon,
  ready: CheckCircleIcon,
  ok: CheckCircleIcon,
  deployed: CheckCircleIcon,
  active: CheckCircleIcon,
  promoted: CheckCircleIcon,

  failed: ErrorIcon,
  error: ErrorIcon,
  cancelled: RemoveCircleOutlineIcon,

  queued: HourglassEmptyIcon,
  skipped: PauseCircleOutlineIcon,
  paused: PauseCircleOutlineIcon,
  archived: PauseCircleOutlineIcon,

  started: PlayArrowIcon,
  running: PlayArrowIcon,
  in_progress: PlayArrowIcon,
  training: PlayArrowIcon,
  deploying: RocketLaunchIcon,
  pending: HourglassEmptyIcon,

  serving: SensorsIcon,
  live: SensorsIcon,

  inactive: PauseCircleOutlineIcon,
  idle: CloudQueueIcon,
  draft: HelpOutlineIcon,
  unknown: HelpOutlineIcon,
};

const TONE_COLORS = (theme) => ({
  success: {
    fg: theme.palette.success.main,
    bg: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
    bd: alpha(theme.palette.success.main, 0.35),
  },
  error: {
    fg: theme.palette.error.main,
    bg: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
    bd: alpha(theme.palette.error.main, 0.35),
  },
  warning: {
    fg: theme.palette.warning.main,
    bg: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.14),
    bd: alpha(theme.palette.warning.main, 0.35),
  },
  info: {
    fg: theme.palette.info.main,
    bg: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
    bd: alpha(theme.palette.info.main, 0.35),
  },
  primary: {
    fg: theme.palette.primary.main,
    bg: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
    bd: alpha(theme.palette.primary.main, 0.35),
  },
  neutral: {
    fg: theme.palette.text.secondary,
    bg: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.10 : 0.06),
    bd: alpha(theme.palette.text.primary, 0.18),
  },
});

const humanize = (s) =>
  String(s || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StatusChip = ({
  status,
  tone,
  label,
  tooltip,
  icon: iconOverride,
  size = 'sm',
  variant = 'soft',
  sx,
}) => {
  const theme = useTheme();
  const normalised = String(status || '').toLowerCase().trim();
  const resolvedTone = tone || STATUS_TONE[normalised] || 'neutral';
  const palette = TONE_COLORS(theme)[resolvedTone] || TONE_COLORS(theme).neutral;
  const Icon = iconOverride !== undefined ? iconOverride : STATUS_ICON[normalised];
  const text = label !== undefined
    ? label
    : (STATUS_LABEL[normalised] || humanize(status));

  const sizeStyles = size === 'md'
    ? { fontSize: 12, px: 1, py: 0.4, iconSize: 14, gap: 0.625 }
    : { fontSize: 11, px: 0.875, py: 0.25, iconSize: 12, gap: 0.5 };

  const variantStyles = (() => {
    switch (variant) {
      case 'outlined':
        return { backgroundColor: 'transparent', color: palette.fg, border: `1px solid ${palette.bd}` };
      case 'solid':
        return { backgroundColor: palette.fg, color: theme.palette.getContrastText(palette.fg), border: '1px solid transparent' };
      case 'soft':
      default:
        return { backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}` };
    }
  })();

  const chip = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizeStyles.gap,
        px: sizeStyles.px,
        py: sizeStyles.py,
        borderRadius: 999,
        fontFamily: theme.typography.fontFamily,
        fontWeight: 600,
        fontSize: sizeStyles.fontSize,
        letterSpacing: 0.2,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...variantStyles,
        ...sx,
      }}
    >
      {Icon ? <Icon sx={{ fontSize: sizeStyles.iconSize }} /> : null}
      <Typography component="span" variant="inherit" sx={{ fontWeight: 'inherit' }}>
        {text || '—'}
      </Typography>
    </Box>
  );

  if (tooltip) {
    return <Tooltip title={tooltip} arrow placement="top">{chip}</Tooltip>;
  }
  return chip;
};

export default StatusChip;
